import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth } from '../../hooks/useAuth'
import { Badge } from '../ui/Badge'
import { Button } from '../ui/Button'
import Logo from '../ui/Logo'
import { AlertCircle } from 'lucide-react'

// Enhanced Avatar Component with Profile Image Support and Profile Completion
const EnhancedAvatar = ({ user, onClick, isProfileComplete, profileCompletion }) => {
  if (!user) return <div className="w-8 h-8 sm:w-10 sm:h-10" />

  // Get display name - extract first name for Google users
  const getDisplayName = () => {
    if (user.pi_username) return user.pi_username
    if (user.google_name) {
      // Extract first name from full name
      return user.google_name.split(' ')[0]
    }
    if (user.full_name) {
      return user.full_name.split(' ')[0]
    }
    return 'User'
  }

  const displayName = getDisplayName()

  return (
    <motion.div
      className="flex items-center gap-2 sm:gap-3 cursor-pointer group"
      onClick={onClick}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Avatar Image Container */}
      <div className="relative">
        {(user.profile_image || user.google_picture) ? (
          <img
            src={user.profile_image || user.google_picture}
            alt={user.pi_username || user.google_name || 'User'}
            className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover ring-2 ring-white/20 group-hover:ring-primary-teal/40 transition-all duration-300 shadow-sm"
            onError={(e) => {
              e.target.style.display = 'none'
              e.target.nextSibling.style.display = 'flex'
            }}
          />
        ) : null}

        {/* Fallback to initials */}
        <div
          className={`${!(user.profile_image || user.google_picture) ? 'flex' : 'hidden'} w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-primary-teal to-primary-teal/80 rounded-full items-center justify-center group-hover:scale-105 transition-transform duration-200 shadow-sm`}
        >
          <span className="text-white text-sm sm:text-base font-semibold">
            {(user.pi_username || user.google_name || 'U').charAt(0).toUpperCase()}
          </span>
        </div>

        {/* Profile Completion Indicator - Shows if profile is incomplete */}
        {!isProfileComplete && (
          <motion.div
            className="absolute -top-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 bg-amber-500 rounded-full border-2 border-white shadow-sm flex items-center justify-center"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.5, type: "spring", stiffness: 300 }}
            title={`Profile ${profileCompletion}% complete - Complete to unlock project creation`}
          >
            <AlertCircle className="w-2 h-2 sm:w-3 sm:h-3 text-white" />
          </motion.div>
        )}

        {/* Badge Level Indicator - Shows if profile is complete */}
        {user.badge_level && isProfileComplete && (
          <motion.div
            className="absolute -top-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 bg-primary-teal rounded-full border-2 border-white shadow-sm"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.5, type: "spring", stiffness: 300 }}
          />
        )}
      </div>

      {/* User Info - Responsive Display */}
      <div className="hidden sm:block text-right">
        <p className="text-p2-m text-neutral-900 group-hover:text-primary-teal transition-colors duration-200">
          {displayName}
        </p>
        {isProfileComplete ? (
          user.badge_level && (
            <Badge variant="secondary" className="text-xs mt-0.5">
              Level {user.badge_level}
            </Badge>
          )
        ) : (
          <Badge variant="outline" className="text-xs mt-0.5 text-amber-600 border-amber-300 bg-amber-50">
            {profileCompletion}% complete
          </Badge>
        )}
      </div>
    </motion.div>
  )
}

// Responsive Logo Component
const ResponsiveLogo = ({ onClick }) => {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
      className="flex items-center flex-shrink-0"
    >
      {/* Mobile: Compact logo */}
      <div className="block sm:hidden">
        <Logo
          variant="mark"
          size="small"
          clickable={true}
          onClick={onClick}
          className="cursor-pointer hover:opacity-80 transition-opacity duration-200"
          alt="Crowblock - Navigate to Dashboard"
        />
      </div>

      {/* Tablet and up: Full logo */}
      <div className="hidden sm:block">
        <Logo
          variant="full"
          size="medium"
          clickable={true}
          onClick={onClick}
          className="cursor-pointer hover:opacity-80 transition-opacity duration-200"
          alt="Crowblock - Navigate to Dashboard"
        />
      </div>
    </motion.div>
  )
}

// Responsive Page Title Component
const ResponsivePageTitle = ({ title }) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="flex-1 text-center px-2 sm:px-4"
    >
      <h2 className="text-sm sm:text-h4 font-heading text-neutral-900 font-semibold truncate">
        {title}
      </h2>
    </motion.div>
  )
}

const TopNavbar = () => {
  const { user, isAuthenticated, isProfileComplete, getProfileCompletion } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  const profileComplete = isProfileComplete()
  const profileCompletion = getProfileCompletion()

  // Map routes to page titles
  const getPageTitle = () => {
    const path = location.pathname
    const titleMap = {
      '/dashboard': 'Dashboard',
      '/projects': 'Projects',
      '/create': 'Create Project',
      '/profile': 'Profile',
      '/donations': 'Donations',
      '/my-projects': 'My Projects'
    }
    return titleMap[path] || 'CROWBLOCK'
  }

  // Handle avatar click - could open user menu or navigate to profile
  const handleAvatarClick = () => {
    navigate('/profile')
  }

  return (
    <motion.header
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="fixed top-0 left-0 right-0 z-50 premium-navbar-glass"
      style={{
        background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.10) 100%)',
        backdropFilter: 'blur(24px) saturate(180%)',
        WebkitBackdropFilter: 'blur(24px) saturate(180%)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.25)'
      }}
    >
      <div className="px-3 sm:px-4 lg:px-6">
        <div className="flex justify-between items-center h-14 sm:h-16 lg:h-18">
          {/* Responsive Logo */}
          <ResponsiveLogo onClick={() => navigate('/dashboard')} />

          {/* Responsive Page Title - Always Visible */}
          <ResponsivePageTitle title={getPageTitle()} />

          {/* Enhanced User Avatar */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex items-center flex-shrink-0"
          >
            {isAuthenticated ? (
              <EnhancedAvatar
                user={user}
                onClick={handleAvatarClick}
                isProfileComplete={profileComplete}
                profileCompletion={profileCompletion}
              />
            ) : (
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate('/login')}
                className="bg-primary-teal hover:bg-primary-teal/90 text-white rounded-full px-4 h-9 sm:h-10 text-xs sm:text-sm font-medium transition-all duration-300 hover:scale-105 shadow-md shadow-primary-teal/20"
              >
                Sign In
              </Button>
            )}
          </motion.div>
        </div>
      </div>

      {/* Subtle bottom gradient line */}
      <div
        className="absolute bottom-0 left-0 right-0 h-px opacity-30"
        style={{
          background: 'linear-gradient(90deg, transparent, rgba(42, 157, 143, 0.3), transparent)'
        }}
      />
    </motion.header>
  )
}

export default TopNavbar
